import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ChevronDown } from 'lucide-react';
import { SubItem } from './types';

interface SearchOverlayProps {
  isMobileSearchFocused: boolean;
  setIsMobileSearchFocused: (focused: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearchSubmit: (query: string) => void;
  recentSearches: string[];
  clearRecentSearches: () => void;
  filteredSuggestions: SubItem[];
  scrollToSection: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => void;
  setShowSuggestions: (show: boolean) => void;
}

export default function SearchOverlay({
  isMobileSearchFocused,
  setIsMobileSearchFocused,
  searchQuery,
  setSearchQuery,
  handleSearchSubmit,
  recentSearches,
  clearRecentSearches,
  filteredSuggestions,
  scrollToSection,
  setShowSuggestions
}: SearchOverlayProps) {
  return (
    <AnimatePresence>
      {isMobileSearchFocused && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-white/98 backdrop-blur-xl p-4 md:p-6 lg:hidden"
          dir="rtl"
        >
          <div className="flex items-center gap-4 mb-8" role="search">
            <div className="relative flex-1">
              <input 
                autoFocus
                type="text" 
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                placeholder="بحث عن خدمة ..." 
                className="w-full bg-slate-100 border border-slate-300 text-brand-dark text-lg rounded-2xl py-4 px-6 pr-14 focus:outline-none focus:border-brand-accent transition-all shadow-[0_0_20px_rgba(0,0,0,0.3)]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit(searchQuery);
                    setIsMobileSearchFocused(false);
                  }
                }}
              />
              <Search className="absolute right-5 top-1/2 -translate-y-1/2 text-brand-accent" size={24} />
            </div>
            <button 
              onClick={() => setIsMobileSearchFocused(false)}
              className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100 text-slate-400 hover:text-brand-dark transition-all"
            >
              <X size={24} />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(100vh-160px)] custom-scrollbar">
            {searchQuery.trim() === '' && recentSearches.length > 0 && (
              <motion.div 
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <p className="text-xs text-brand-dark/40 uppercase tracking-widest font-black">عمليات البحث الأخيرة</p>
                  <button 
                    onClick={clearRecentSearches}
                    className="text-xs text-brand-accent font-bold"
                  >
                    مسح السجل
                  </button>
                </div>
                <div className="space-y-1">
                  {recentSearches.map((search, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setSearchQuery(search);
                        handleSearchSubmit(search);
                        setIsMobileSearchFocused(false);
                      }}
                      className="w-full text-right p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-2xl transition-all flex items-center gap-4 group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300 group-hover:text-brand-accent transition-all">
                        <Search size={18} />
                      </div>
                      <span className="text-brand-dark/70 group-hover:text-brand-dark font-medium transition-colors">{search}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="space-y-4">
              {filteredSuggestions.length > 0 ? (
                <>
                  <p className="text-xs text-brand-dark/40 px-2 uppercase tracking-widest font-black mb-4">الاقتراحات</p>
                  <div className="grid grid-cols-1 gap-2">
                    {filteredSuggestions.map((item, i) => (
                    <motion.a
                      layout
                      key={i}
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : '_self'}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : ''}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        onClick={(e) => {
                          handleSearchSubmit(item.name);
                          setIsMobileSearchFocused(false);
                          scrollToSection(e, item.href);
                        }}
                        className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-2xl transition-all group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-brand-accent group-hover:bg-brand-accent group-hover:text-brand-dark transition-all duration-300">
                            {item.icon && <item.icon size={22} />}
                          </div>
                          <div>
                            <p className="text-brand-dark font-bold">{item.name}</p>
                            <p className="text-[10px] text-slate-300 uppercase tracking-wider">{item.type}</p>
                          </div>
                        </div>
                        <ChevronDown size={18} className="-rotate-90 text-slate-200 group-hover:text-brand-accent transition-colors" />
                      </motion.a>
                    ))}
                  </div>
                </>
              ) : searchQuery.trim() !== '' && (
                <div className="py-20 text-center">
                  <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6 text-slate-200">
                    <Search size={32} />
                  </div>
                  <p className="text-brand-dark/40 font-bold">عذراً، لم نجد نتائج لـ \"{searchQuery}\"</p>
                  <p className="text-xs text-slate-200 mt-2">حاول البحث بكلمات أبسط أو مختلفة</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
